import { userSession } from 'state/session';
import { effectiveHostSlotId } from 'state/session/streamHostSlot';
import { getVoipSlotId } from 'utils/voipTabId';
import { user } from 'utils/auth/user';
import { voipRtcConnection } from 'utils/ui/rtc';
import { hasLiveMasterPeer } from 'utils/ui/voipRemotes';

/**
 * AVS-4482: a 3D stream loss in a tab that does not host the stream stays in that tab while the tab
 * that hosts it is connected, because that tab carries the session. A non-host's loss ends nothing for
 * anyone else; only the host's own live row counts. AVS-4485: the host is the dealer's host slot when
 * one is occupied (a hidden master handed 3D to the tab the dealer uses), else the VOIP master. So the
 * hidden old master's loss (Cirrus 4006 included) stays local, and the host's own loss, a lone tab's,
 * or a loss whose host is gone still ends 3D for the session as before.
 */
export function isDeckFailureLocal(): boolean {
  const session = userSession();
  if (!user.isDealer || !session.isVoipSingleStreamEnabled) return false;
  const hostSlotId = effectiveHostSlotId(
    session.get('streamHosts')?.[user.userPseudonym],
    session.get('voipMasters')?.[user.userPseudonym]
  );
  if (!hostSlotId || hostSlotId === getVoipSlotId(session.get('id'))) return false;
  const remotes = Object.values(voipRtcConnection?.store.get('remoteConnections') ?? {});
  return hasLiveMasterPeer(remotes, hostSlotId);
}

const LOCAL_DECK_RETRY_DELAYS_MS = [5000, 10000, 20000];

/**
 * AVS-4482: how long a visible tab waits before it re-mounts a locally failed deck, by retry number.
 * `undefined` once the retries are spent: the deck then waits for the dealer (visibility, a new stream, take over).
 */
export function localDeckRetryDelay(attempt: number): number | undefined {
  return LOCAL_DECK_RETRY_DELAYS_MS[attempt];
}
